'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { Checkbox } from '@govinity/ui/components/ui/checkbox';
import { Label } from '@govinity/ui/components/ui/label';
import { Skeleton } from '@govinity/ui/components/ui/skeleton';
import { LoadingError } from '@govinity/ui/components/ui-custom/loading-error';
import type { MediaPlayerRef } from '@govinity/ui/components/modules/media-player';
import { MediaPlayer } from '@govinity/ui/components/modules/media-player';
import type { MuxVideoPlayerRef } from '@govinity/ui/components/modules/mux-video-player';
import { MuxVideoPlayer } from '@govinity/ui/components/modules/mux-video-player';
import { MeetingEventDocumentType, MeetingStatus } from '@govinity/validation';
import type { MeetingEventPublicGetResponse } from '@govinity/validation/public';
import { FloatingVideoPlayer } from '@govinity/ui/components/modules/floating-video-player';
import {
  TranslatableDocumentsProvider,
  translationPollInterval,
} from '@/components/ui/translatable-documents';
import { cn } from '@/lib/utils';
import { getCaptionMasterUrl } from '@/lib/utils/caption-proxy';
import { getPageCaptionLanguage } from '@/lib/utils/page-caption-language';
import api from '@/lib/api';
import { env } from '@/env.mjs';
import {
  buildAgendaLookup,
  resolveCurrentLiveItemLabel,
  resolveTimestampLabel,
} from './meeting-agenda-utils';
import { MeetingContext, type MeetingContextValue } from './meeting-context';
import { MeetingHeader } from './meeting-header';
import { hasLiveVideo as deriveHasLiveVideo } from './lib/live-video-state';
import { useMachineTranslationNotice } from './lib/use-machine-translation-notice';
import { CommentStatusCard } from './public-comments/comment-status-card';
import { SignupStatusCard } from './public-speaker-signup/signup-status-card';
import { useGovernmentContext } from '../../context';

type TabConfig = { id: string; label: string; href: string };

export function MeetingLayout({ children }: { children: React.ReactNode }) {
  const t = useTranslations('MeetingLayout');
  const params = useParams();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { government } = useGovernmentContext();
  const handleCaptionLanguageChange = useMachineTranslationNotice();
  const locale = useLocale();
  const pageCaptionLanguage = getPageCaptionLanguage(locale);
  const meetingIdParam = params?.id as string;
  const meetingId = Number(meetingIdParam);
  const code = params?.code as string;
  const basePath = `/portal/${code}/meetings/${meetingId}`;

  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  // Two separate refs — floating (desktop) and embedded (mobile) — so seeks always
  // target the visible player regardless of which one holds the ref at mount time.
  const floatingPlayerRef = useRef<MuxVideoPlayerRef | MediaPlayerRef | null>(null);
  const embeddedPlayerRef = useRef<MuxVideoPlayerRef | MediaPlayerRef | null>(null);
  const [showVideo, setShowVideo] = useState(false);

  // ?t=<seconds> deep-link: seek the player to this time once the recording is ready.
  // Parsed once on mount; the seek is deferred until the recording data is loaded.
  const deepLinkTime = useMemo(() => {
    const raw = searchParams.get('t');
    if (!raw) return null;
    const parsed = parseFloat(raw);
    return isFinite(parsed) && parsed >= 0 ? parsed : null;
  }, [searchParams]);

  const deepLinkHighlight = useMemo(() => searchParams.get('hl') ?? undefined, [searchParams]);

  // Measured height of the sticky tab strip — used as the top offset for the
  // mobile sticky video / "Now:" / Show Layers stack so it pins flush below it.
  const tabStripRef = useRef<HTMLDivElement>(null);
  const [tabStripHeight, setTabStripHeight] = useState(44);

  const handleVideoSeek = useCallback((time: number) => {
    // md breakpoint = 768px; read at call time so resize is always handled correctly
    const isMobile = window.innerWidth < 768;
    (isMobile ? embeddedPlayerRef : floatingPlayerRef).current?.seek(time);
  }, []);

  // ─── Fetch meeting ─────────────────────────────────────────────────────────
  const meetingQueryKey = useMemo(
    () => ['meeting', government.code, meetingId],
    [government.code, meetingId]
  );
  const {
    data: meeting,
    isLoading,
    error,
  } = useQuery({
    enabled: !!government.code && !!meetingId,
    queryKey: meetingQueryKey,
    queryFn: () => api.governments.meetings(government.code).getById(meetingId),
    refetchInterval: translationPollInterval(
      (data: MeetingEventPublicGetResponse) => data.meetingEventDocuments
    ),
  });
  const isMeetingLive = meeting?.status === MeetingStatus.Live;
  const isCancelled = meeting?.status === MeetingStatus.Cancelled;

  // ─── Fetch live stream ─────────────────────────────────────────────────────
  const {
    data: liveStreamData,
    isSuccess: isLiveStreamSuccess,
    isLoading: isLiveStreamLoading,
  } = useQuery({
    enabled: !!government.code && !!meetingId && isMeetingLive,
    queryKey: ['meeting-live-stream', government.code, meetingId],
    queryFn: () => api.governments.meetings(government.code).getLiveStreamUrl(meetingId),
    retry: false,
  });

  // ─── Fetch recording ───────────────────────────────────────────────────────
  const {
    data: recordingData,
    isSuccess: isRecordingSuccess,
    isLoading: isRecordingLoading,
  } = useQuery({
    enabled: !!government.code && !!meetingId && !!meeting,
    queryKey: ['meeting-recording', government.code, meetingId],
    // A 404 means there is no published recording (never published, or just
    // unpublished), so resolve to null — this clears any previously-cached
    // signed URL. Other (transient) errors throw so React Query keeps the last
    // successful data and the player survives a blip.
    queryFn: async () => {
      try {
        return await api.governments.meetings(government.code).getRecordingUrl(meetingId);
      } catch (error) {
        if ((error as { status?: number } | null)?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    retry: false,
  });

  // ─── Fetch timestamps ──────────────────────────────────────────────────────
  const {
    data: timestamps = [],
    isLoading: isLoadingTimestamps,
    error: timestampsError,
  } = useQuery({
    enabled: !!government.code && !!meetingId && !!meeting,
    queryKey: ['meeting-timestamps', government.code, meetingId],
    queryFn: () => api.governments.meetings(government.code).getTimestamps(meetingId),
    retry: false,
  });

  // ─── Fetch current live item ───────────────────────────────────────────────
  // A live meeting has no media-archive timestamps yet, so the "Now:" indicator can't be derived
  // from the video playhead. Instead it follows the clerk's currently-floored entry, returned here
  // as a section/placement id (no titles — resolved client-side against the already-scrubbed public
  // agenda). The portal realtime provider invalidates this on PUBLIC_MEETING_EVENT_CHANGE, which
  // fires each time an entry is floored, so the indicator advances live.
  const { data: currentLiveItem } = useQuery({
    enabled: !!government.code && !!meetingId && isMeetingLive,
    queryKey: ['meeting-current-live-item', government.code, meetingId],
    queryFn: () => api.governments.meetings(government.code).getCurrentLiveItem(meetingId),
    retry: false,
  });

  const hasLiveVideo = deriveHasLiveVideo({ isMeetingLive, liveStreamData });
  // The recording query resolves to null on a 404, so an unpublish (which 404s)
  // clears the signed URL and hides the player, while a transient error keeps the
  // last successful data and leaves the player up.
  const hasRecordedVideo = !!recordingData?.signedUrl;
  const hasVideo = hasLiveVideo || hasRecordedVideo;
  const recordingPoster = hasRecordedVideo ? (recordingData?.thumbnailUrl ?? undefined) : undefined;

  const hasMinutes = !!meeting?.meetingEventDocuments.some(
    (doc) => doc.type === MeetingEventDocumentType.MINUTES
  );
  const hasTranscript = !!meeting?.meetingEventDocuments.some(
    (doc) => doc.type === MeetingEventDocumentType.TRANSCRIPT
  );
  const hasMediaIndexes = timestamps.length > 0;
  const shouldShowMediaIndexesTab = hasVideo && (hasMediaIndexes || hasTranscript);
  const isLoadingVideoAvailability = isMeetingLive ? isLiveStreamLoading : isRecordingLoading;
  const isResolvingMediaIndexesTabVisibility =
    isLoading || isLoadingVideoAvailability || isLoadingTimestamps;

  // Auto-show video when it becomes available
  useEffect(() => {
    if ((isLiveStreamSuccess || isRecordingSuccess) && hasVideo) {
      setShowVideo(true);
    }
  }, [hasVideo, isLiveStreamSuccess, isRecordingSuccess]);

  // Seek to the deep-link time once the recording is ready and the player has mounted.
  const deepLinkSeeked = useRef(false);
  useEffect(() => {
    if (deepLinkTime === null || deepLinkSeeked.current || !showVideo) return;
    // Delay one tick to give the player ref time to attach after showVideo flips to true.
    const timer = setTimeout(() => {
      handleVideoSeek(deepLinkTime);
      deepLinkSeeked.current = true;
    }, 300);
    return () => clearTimeout(timer);
  }, [deepLinkTime, showVideo, handleVideoSeek]);

  // ─── Agenda sections ───────────────────────────────────────────────────────
  const agendaSections = useMemo(() => {
    if (!meeting?.agenda?.sections) return [];
    return [...meeting.agenda.sections].sort((a, b) => a.orderById - b.orderById);
  }, [meeting?.agenda?.sections]);

  const { sectionMap, itemMap, sectionNumberMap, itemNumberMap, sectionOrderById, itemOrderById } =
    useMemo(() => buildAgendaLookup(agendaSections), [agendaSections]);

  const sortedTimestamps = useMemo(
    () => [...timestamps].sort((a, b) => a.timestampSeconds - b.timestampSeconds),
    [timestamps]
  );

  // ─── "Now:" label ──────────────────────────────────────────────────────────
  // Recorded playback: last timestamp ≤ current video time. Shares resolveTimestampLabel with the
  // media-index timeline so both stay in step: entry-level minutes timestamps (dataType null,
  // event-only) resolve to their numbered section/item, while minute-record timestamps (roll call,
  // note, motion) return null and don't move the indicator.
  const recordedNowLabel = useMemo(() => {
    const lookup = { sectionMap, itemMap, sectionNumberMap, itemNumberMap };
    let current = null;
    for (const ts of sortedTimestamps) {
      if (ts.timestampSeconds > videoCurrentTime) break;
      const label = resolveTimestampLabel(ts, lookup);
      if (label) current = label;
    }
    return current;
  }, [sortedTimestamps, videoCurrentTime, sectionMap, itemMap, sectionNumberMap, itemNumberMap]);

  // Live meeting: no timestamps exist yet, so the indicator follows the clerk's floored entry,
  // resolved against the same agenda lookup maps as the recorded path so both read identically.
  const liveNowLabel = useMemo(
    () =>
      resolveCurrentLiveItemLabel(currentLiveItem ?? null, {
        sectionMap,
        itemMap,
        sectionNumberMap,
        itemNumberMap,
      }),
    [currentLiveItem, sectionMap, itemMap, sectionNumberMap, itemNumberMap]
  );

  const nowLabel = isMeetingLive ? liveNowLabel : recordedNowLabel;

  // ─── Tab visibility and routing ────────────────────────────────────────────
  const tabOptions = useMemo<TabConfig[]>(() => {
    const tabs: TabConfig[] = [{ id: 'agenda', label: t('agendaTab'), href: basePath }];

    if (!isCancelled) {
      tabs.push({ id: 'minutes', label: t('minutesTab'), href: `${basePath}/minutes` });
    }

    if (shouldShowMediaIndexesTab) {
      tabs.push({
        id: 'media-indexes',
        label: t('mediaIndexesTab'),
        href: `${basePath}/media-indexes`,
      });
    }

    if (env.NEXT_PUBLIC_APP_ENV !== 'production') {
      tabs.push({ id: 'history', label: t('historyTab'), href: `${basePath}/history` });
    }
    tabs.push({ id: 'attachments', label: t('attachmentsTab'), href: `${basePath}/attachments` });

    return tabs;
  }, [basePath, isCancelled, shouldShowMediaIndexesTab, t]);

  const activeTab = useMemo(() => {
    const currentPath = pathname;
    if (currentPath === basePath) return 'agenda';
    if (currentPath === `${basePath}/minutes`) return 'minutes';
    if (currentPath === `${basePath}/media-indexes`) return 'media-indexes';
    if (currentPath === `${basePath}/history`) return 'history';
    if (currentPath === `${basePath}/attachments`) return 'attachments';
    return 'agenda';
  }, [pathname, basePath]);

  useLayoutEffect(() => {
    const el = tabStripRef.current;
    if (!el) return;

    const measure = () => setTabStripHeight(Math.round(el.getBoundingClientRect().height));
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeTab, tabOptions.length, hasVideo, isLoading, meeting?.id]);

  useEffect(() => {
    if (activeTab === 'media-indexes' && isResolvingMediaIndexesTabVisibility) return;

    const isActiveTabVisible = tabOptions.some(({ id }) => id === activeTab);
    if (!isActiveTabVisible) {
      router.replace(basePath);
    }
  }, [activeTab, tabOptions, isResolvingMediaIndexesTabVisibility, basePath, router]);

  const meetingContextValue = useMemo((): MeetingContextValue | null => {
    if (!meeting) return null;

    return {
      meeting,
      agendaSections,
      sectionMap,
      itemMap,
      sectionNumberMap,
      itemNumberMap,
      sectionOrderById,
      itemOrderById,
      timestamps,
      isLoadingTimestamps,
      timestampsError: timestampsError as Error | null,
      videoCurrentTime,
      handleVideoSeek,
      hasVideo,
      hasLiveVideo,
      showVideo,
      nowLabel,
      tabStripHeight,
      deepLinkTime,
      deepLinkHighlight,
      liveStreamData,
      recordingData: recordingData ?? undefined,
      floatingPlayerRef,
    };
  }, [
    meeting,
    agendaSections,
    sectionMap,
    itemMap,
    sectionNumberMap,
    itemNumberMap,
    sectionOrderById,
    itemOrderById,
    timestamps,
    isLoadingTimestamps,
    timestampsError,
    videoCurrentTime,
    handleVideoSeek,
    hasVideo,
    hasLiveVideo,
    showVideo,
    nowLabel,
    tabStripHeight,
    deepLinkTime,
    deepLinkHighlight,
    liveStreamData,
    recordingData,
  ]);

  // ─── Loading / error states ────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <Skeleton className="mb-4 h-32 w-full" />
        <Skeleton className="mb-6 h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <LoadingError message={error instanceof Error ? error.message : t('failedToLoadMeeting')} />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-foreground mb-2 text-2xl font-bold">Meeting Not Found</h1>
          <p className="text-muted-foreground">The requested meeting could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <MeetingContext.Provider value={meetingContextValue}>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Meeting identity card */}
        <MeetingHeader
          meeting={meeting}
          hasVideo={hasVideo}
          hasMinutes={hasMinutes}
          isOnAir={liveStreamData?.isOnAir}
          isRecessed={meeting?.isRecessed}
        />

        <SignupStatusCard meeting={meeting} />

        <CommentStatusCard meeting={meeting} />

        {/* ─── Mobile video section — above tabs, hidden on desktop ─────────── */}
        {hasVideo && (
          <div className="mb-4 md:hidden">
            <div className="mb-2 flex items-center justify-end gap-1.5">
              <Checkbox
                id="mobile-video-toggle"
                checked={showVideo}
                onCheckedChange={(checked) => setShowVideo(checked === true)}
                aria-label={t('toggleVideoPlayerAriaLabel')}
              />
              <Label htmlFor="mobile-video-toggle" className="cursor-pointer text-sm">
                Video
              </Label>
            </div>
            {showVideo && (
              <div className="overflow-hidden rounded-lg">
                <div className="aspect-video bg-black">
                  {hasLiveVideo ? (
                    <MuxVideoPlayer
                      ref={embeddedPlayerRef as React.Ref<MuxVideoPlayerRef>}
                      playbackId={liveStreamData!.muxPlaybackId!}
                      src={getCaptionMasterUrl(government.code, liveStreamData!.muxPlaybackId!)}
                      envKey={env.NEXT_PUBLIC_MUX_DATA_ENV_KEY}
                      streamType="live"
                      isStreamActive={true}
                      autoplay
                      muted
                      onTimeUpdate={setVideoCurrentTime}
                      onCaptionLanguageChange={handleCaptionLanguageChange}
                      preferredCaptionLanguage={pageCaptionLanguage}
                      metadata={{
                        videoId: `meeting-live-${meetingId}`,
                        videoTitle: `${meeting.title} Live Stream`,
                      }}
                    />
                  ) : recordingData?.muxPlaybackId ? (
                    <MuxVideoPlayer
                      ref={embeddedPlayerRef as React.Ref<MuxVideoPlayerRef>}
                      playbackId={recordingData.muxPlaybackId}
                      src={getCaptionMasterUrl(government.code, recordingData.muxPlaybackId)}
                      envKey={env.NEXT_PUBLIC_MUX_DATA_ENV_KEY}
                      poster={recordingPoster}
                      streamType="on-demand"
                      onTimeUpdate={setVideoCurrentTime}
                      onCaptionLanguageChange={handleCaptionLanguageChange}
                      preferredCaptionLanguage={pageCaptionLanguage}
                      metadata={{
                        videoId: `meeting-${meetingId}`,
                        videoTitle: meeting.title,
                      }}
                    />
                  ) : recordingData?.signedUrl ? (
                    <MediaPlayer
                      ref={embeddedPlayerRef as React.Ref<MediaPlayerRef>}
                      poster={recordingPoster}
                      src={recordingData.signedUrl}
                      onTimeUpdate={setVideoCurrentTime}
                    />
                  ) : null}
                </div>
                {/* Live "Now:" indicator — the media-indexes tab (which hosts the recorded Now bar)
                    is hidden during a live meeting, so mirror it here beneath the live video. */}
                {hasLiveVideo && (
                  <div className="border-border bg-primary/5 flex items-center gap-1.5 border-t px-3 py-2">
                    <span className="text-primary shrink-0 text-xs font-semibold tracking-wide uppercase">
                      {t('nowLabel')}
                    </span>
                    <span className="text-foreground min-w-0 flex-1 truncate text-xs">
                      {nowLabel ?? (
                        <span className="text-muted-foreground italic">{t('noActiveItem')}</span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── Horizontal tab strip ──────────────────────────────────────────── */}
        {/* Transparent and flush with the panel so the active tab covers the card's top ring and joins it. */}
        <div ref={tabStripRef} className="relative z-30">
          <div className="flex items-end">
            <nav
              aria-label={t('meetingSectionsAriaLabel')}
              className="flex flex-1 [scrollbar-width:none] items-end gap-1 overflow-x-auto px-3 [&::-webkit-scrollbar]:hidden"
            >
              {tabOptions.map(({ id, label, href }) => {
                const isActive = activeTab === id;
                return (
                  <Link
                    key={id}
                    href={href}
                    id={`meeting-tab-${id}`}
                    aria-current={isActive ? 'page' : undefined}
                    className={cn(
                      'relative flex shrink-0 items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'border-border bg-card text-foreground z-10 border-t border-r border-l'
                        : 'text-muted-foreground hover:text-foreground border border-transparent'
                    )}
                  >
                    {label}
                  </Link>
                );
              })}
            </nav>

            {/* Video toggle — right-aligned, desktop only */}
            {hasVideo && (
              <div className="hidden shrink-0 items-center gap-1.5 px-3 pb-1 md:flex">
                <Checkbox
                  id="video-toggle"
                  checked={showVideo}
                  onCheckedChange={(checked) => setShowVideo(checked === true)}
                  aria-label={t('toggleVideoPlayerAriaLabel')}
                />
                <Label htmlFor="video-toggle" className="cursor-pointer text-sm">
                  Video
                </Label>
              </div>
            )}
          </div>
        </div>

        {/* ─── Tab content ────────────────────────────────────────────────────── */}
        <div id={`meeting-tabpanel-${activeTab}`}>
          <TranslatableDocumentsProvider
            queryKey={meetingQueryKey}
            requestTranslation={(documentId) =>
              api.governments
                .meetings(government.code)
                .requestDocumentTranslation(meetingId, documentId)
            }
          >
            {children}
          </TranslatableDocumentsProvider>
        </div>

        {/* ─── Floating video player (desktop only) ───────────────────────────── */}
        <FloatingVideoPlayer
          visible={showVideo && hasVideo}
          className="hidden md:block"
          livePlaybackId={hasLiveVideo ? (liveStreamData?.muxPlaybackId ?? undefined) : undefined}
          liveSrc={
            hasLiveVideo && liveStreamData?.muxPlaybackId
              ? getCaptionMasterUrl(government.code, liveStreamData.muxPlaybackId)
              : undefined
          }
          isStreamActive={hasLiveVideo ? liveStreamData?.isStreamActive : undefined}
          recordingPlaybackId={!hasLiveVideo ? recordingData?.muxPlaybackId : undefined}
          envKey={env.NEXT_PUBLIC_MUX_DATA_ENV_KEY}
          recordingSrc={
            !hasLiveVideo && recordingData?.muxPlaybackId
              ? getCaptionMasterUrl(government.code, recordingData.muxPlaybackId)
              : undefined
          }
          recordingSignedUrl={!hasLiveVideo ? recordingData?.signedUrl : undefined}
          poster={recordingPoster}
          meetingId={meetingId}
          meetingTitle={meeting.title}
          nowLabel={nowLabel}
          playerRef={floatingPlayerRef}
          onTimeUpdate={setVideoCurrentTime}
          onCaptionLanguageChange={handleCaptionLanguageChange}
          preferredCaptionLanguage={pageCaptionLanguage}
        />
      </div>
    </MeetingContext.Provider>
  );
}
